import React, { useRef } from 'react'
import { Box } from '@mui/material'
import { C, TONE, tnum } from './tokens'
import { STATUS_TABS } from './model'

/**
 * Segmented control: one status at a time, each with its live count. A count
 * of null means "not known yet" (history not loaded under a filter) and shows
 * no pill rather than a wrong number. `tabs` defaults to every status tab.
 */
export default function StatusTabs({ tabs = STATUS_TABS, value, counts, onChange, loading }) {
  const refs = useRef([])

  const onKeyDown = (e, i) => {
    const dir = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0
    if (!dir) return
    e.preventDefault()
    const next = (i + dir + tabs.length) % tabs.length
    if (refs.current[next]) refs.current[next].focus()
    onChange(tabs[next].key)
  }

  return (
    <Box sx={{ overflowX: 'auto', mx: -0.5, px: 0.5, py: 0.25, scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}>
      <Box role="tablist" aria-label="Order status" sx={{ display: 'inline-flex', alignItems: 'center', gap: '2px', p: '3px', borderRadius: '10px', bgcolor: C.mutedBg }}>
        {tabs.map((t, i) => {
          const on = t.key === value
          const n = counts[t.key]
          const dot = t.tone ? TONE[t.tone].dot : null
          return (
            <Box
              key={t.key}
              component="button"
              type="button"
              role="tab"
              aria-selected={on}
              tabIndex={on ? 0 : -1}
              ref={el => { refs.current[i] = el }}
              onClick={() => onChange(t.key)}
              onKeyDown={e => onKeyDown(e, i)}
              sx={{
                display: 'inline-flex', alignItems: 'center', gap: 1, height: 32, px: 1.25, border: 0, borderRadius: '8px',
                font: 'inherit', fontSize: 13, fontWeight: 500, whiteSpace: 'nowrap', cursor: 'pointer',
                color: on ? C.text : C.muted,
                bgcolor: on ? C.card : 'transparent',
                boxShadow: on ? `0 1px 2px rgba(16,24,40,0.08), 0 0 0 1px ${C.border}` : 'none',
                transition: 'color 120ms, background-color 120ms',
                '&:hover': { color: C.text },
                '&:focus-visible': { outline: 'none', boxShadow: `0 0 0 2px ${C.input}` },
              }}
            >
              {dot && <Box component="span" aria-hidden sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: dot, opacity: !on && n === 0 ? 0.4 : 1 }} />}
              {t.label}
              {n != null && (
                <Box
                  component="span"
                  sx={{
                    minWidth: 20, px: 0.75, borderRadius: 999, textAlign: 'center', fontSize: 11, lineHeight: '16px', fontWeight: 600, ...tnum,
                    opacity: loading ? 0.4 : 1,
                    ...(on
                      ? { bgcolor: C.ink, color: '#fff' }
                      : n === 0
                        ? { color: 'rgba(93,102,121,0.6)' }
                        : { bgcolor: 'rgba(16,24,40,0.07)', color: 'rgba(16,24,40,0.75)' }),
                  }}
                >
                  {n}
                </Box>
              )}
            </Box>
          )
        })}
      </Box>
    </Box>
  )
}
